/**
 * The finishes laid ON a wall — a backsplash band, shower or tub tile, a
 * hand-drawn tile or paint section — as an elevation draws them (item #1).
 * Pure; importable with no environment.
 *
 * A region is a percent rect of its wall (`room.walls[].texture.regions`).
 * What those percentages MEAN is decided by the 3D view, which is what the
 * user sees: `textureGeometry` maps a region's x along `wallRight` from
 * `getWallCoordinateSystem` (the wall face's low end at 0 %) and its y up from
 * the face's bottom. So each region is placed in that frame, as two WORLD plan
 * points plus a height band, and the renderer projects the points with
 * whatever basis it draws objects with — exactly as it does an end-on wall
 * band. The drawing therefore cannot disagree with the 3D.
 *
 * Not the band BUILDER's frame (`backsplashRegion.getWallEndpointsXZ`) and not
 * the take-off's (`geometry/wallAxis`): both prefer `alignment` and fall back
 * to their own direction, and the builder's fallback walks a wall along
 * (cos, +sin) where the 3D walks (cos, −sin) — opposite ends at ±90°. Where
 * those disagree with the 3D, the 3D is what was painted.
 */
import { getWallCoordinateSystem } from './meshDataHelpers';
import {
  isOverlayRegion,
  isShowerSurfaceRegion,
} from '@shared-lib/estimate-pricing/designCenter/surfaceRegions';
import {
  regionHasAssignedFinish,
  isPaintRegion,
} from '@shared-lib/estimate-pricing/utils/roomTakeOffs';
import {
  regionRectOnWall,
  assignWallBlockers,
  blockerCutsFinish,
  objAlongExtent,
} from '@shared-lib/millwork';
import { cardBandOf, isTileSection, SECTION_BAND } from './elevationBands';
import { openingHole } from '../components/3d/geometry/openingHole';
import { belongsToWall } from './parentWallHelpers';
import { openingTypeOf } from './openingTypeConversion';

const dimsOf = (wall) => {
  const d = wall?.dimensions;
  if (Array.isArray(d)) return { x: d[0] || 0, y: d[1] || 0 };
  return { x: d?.x || 0, y: d?.y || 0 };
};

/**
 * The wall face's extent along `right` and up — the u/v ranges the 3D tiles a
 * region over. The face polygon where the wall has one (`textureGeometry`
 * reads the mesh, which is built from it), else the stored rectangle.
 */
const faceRange = (wall, right) => {
  const corners = wall.polygonCorners;
  if (Array.isArray(corners) && corners.length >= 3) {
    let minU = Infinity;
    let maxU = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;
    for (const c of corners) {
      const u = (c.x || 0) * right.x + (c.z || 0) * right.z;
      minU = Math.min(minU, u);
      maxU = Math.max(maxU, u);
      minV = Math.min(minV, c.y || 0);
      maxV = Math.max(maxV, c.y || 0);
    }
    if (maxU > minU && maxV > minV) return { minU, maxU, minV, maxV };
  }
  const { x: len, y: height } = dimsOf(wall);
  const c = wall.center;
  if (!c || !(len > 0) || !(height > 0)) return null;
  const mid = (c.x || 0) * right.x + (c.z || 0) * right.z;
  return {
    minU: mid - len / 2,
    maxU: mid + len / 2,
    minV: (c.y || 0) - height / 2,
    maxV: (c.y || 0) + height / 2,
  };
};

// a paint region's hex: its own colour, else the hex its `paint_<HEX>` id carries
const paintHex = (region) => {
  if (region.color) return region.color;
  const m = /^paint_([0-9a-f]{6})$/i.exec(region.id || '');
  return m ? `#${m[1]}` : null;
};

/** What a finish is, in a word the drawing can print. */
export const finishKindOf = (region) => {
  if (isPaintRegion(region)) return { kind: 'paint', label: 'Paint' };
  const surface = region.surfaceType || region.source;
  if (surface === 'backsplash')
    return { kind: 'backsplash', label: 'Backsplash' };
  if (surface === 'tubBacksplash')
    return { kind: 'tub', label: 'Tub surround' };
  if (isShowerSurfaceRegion(region) || surface === 'showerWall') {
    return { kind: 'shower', label: 'Shower tile' };
  }
  return { kind: 'tile', label: 'Tile' };
};

/**
 * `rect` less every hole in it: the pieces left, as rectangles in the same
 * frame (`{a0, a1, y0, y1}`). Each hole cuts every piece it overlaps into up
 * to four — beside it on either side, below and above it — so the pieces never
 * overlap one another or a hole.
 */
export function cutRect(rect, holes) {
  let pieces = [rect];
  for (const h of holes || []) {
    const next = [];
    for (const p of pieces) {
      const a0 = Math.max(p.a0, h.a0);
      const a1 = Math.min(p.a1, h.a1);
      const y0 = Math.max(p.y0, h.y0);
      const y1 = Math.min(p.y1, h.y1);
      if (!(a1 > a0) || !(y1 > y0)) {
        next.push(p);
        continue;
      }
      if (a0 > p.a0) next.push({ ...p, a1: a0 });
      if (a1 < p.a1) next.push({ ...p, a0: a1 });
      if (y0 > p.y0) next.push({ ...p, a0, a1, y1: y0 });
      if (y1 < p.y1) next.push({ ...p, a0, a1, y0: y1 });
    }
    pieces = next;
  }
  return pieces;
}

const OPENING_KINDS = new Set(['door', 'window', 'opening']);
/** What an opening is — its `type` as the 3D carve reads it, else its
 * category — or null for anything that is not one. */
export const openingKindOf = (o) =>
  OPENING_KINDS.has(o?.type) ? o.type : openingTypeOf(o);

/**
 * The holes `wall`'s own doors and windows carve in its face, in the frame
 * a region rect is in: metres along from the face's low end, up from its
 * floor. The 3D carve's own rule (`openingHole`), so the drawing cuts a
 * finish exactly where the 3D tile stops.
 */
function openingHolesOnFace(wall, openings, right, range) {
  const holes = [];
  for (const opening of openings || []) {
    const type = openingKindOf(opening);
    if (!type || !belongsToWall(opening, wall.id)) continue;
    const hole = openingHole(opening, {
      type,
      right: { x: right.x, y: 0, z: right.z },
      up: { x: 0, y: 1, z: 0 },
      origin: { x: 0, y: 0, z: 0 },
      faceMinY: range.minV,
      wallRotation: wall.rotation,
    });
    if (!hole || !(hole.right > hole.left) || !(hole.top > hole.bottom)) {
      continue;
    }
    holes.push({
      a0: hole.left - range.minU,
      a1: hole.right - range.minU,
      y0: hole.bottom - range.minV,
      y1: hole.top - range.minV,
    });
  }
  return holes;
}

/**
 * What the take-off takes out of `wall`'s finish for the objects standing
 * against it — by its OWN rule (`assignWallBlockers` + `blockerCutsFinish`):
 * a cabinet or vanity cuts tile and paint, a shower paint only, a toilet or
 * an appliance nothing — as `{tile, paint}` rects in the region frame. The
 * object's extent along the wall is the take-off's (`objAlongExtent`); its
 * place along it, this face's.
 */
function finishCutsOnFace(wall, objects, right, range) {
  const cuts = { tile: [], paint: [] };
  if (!objects?.length) return cuts;
  const against = assignWallBlockers([wall], objects, range.minV).get(wall);
  for (const obj of against || []) {
    const c = obj?.center;
    const ext = objAlongExtent(obj, wall);
    if (!c || !(ext > 0)) continue;
    const u = (c.x || 0) * right.x + (c.z || 0) * right.z - range.minU;
    const h = obj.dimensions?.y || 0;
    const y0 = typeof c.y === 'number' ? c.y - h / 2 - range.minV : 0;
    const rect = { a0: u - ext / 2, a1: u + ext / 2, y0, y1: y0 + h };
    for (const kind of ['tile', 'paint']) {
      if (blockerCutsFinish(obj, kind)) cuts[kind].push(rect);
    }
  }
  return cuts;
}

/**
 * Every finish laid on `wall`, in the 3D's frame: `{ key, kind, label, color,
 * a, b, bottom, top, wallId, regionId, band }` — `a`/`b` the band's two
 * along-wall ends as world plan points, `bottom`/`top` metres above the
 * face's bottom (the drawing's floor line), `color` a paint's hex or null.
 * The wall's own full-coverage finish is not one (it IS the wall), nor is a
 * drafted region with no finish picked.
 *
 * `band` is the Backsplash-card band the region is (`cardBandOf`) when the
 * host says which card the wall has (`cardKind`, call item #28); a tile
 * section — a wainscot, tile drawn in Edit wall — is `SECTION_BAND` on any
 * host (PR 5, `isTileSection`); null for paint and a band whose card is not
 * named. `groupId` says what was created with it.
 *
 * A finish is cut at the wall's own doors and windows (`openings`, hydrated —
 * any opening not on this wall is ignored) by the hole the 3D carves, so a
 * band crossing a doorway is drawn as the pieces either side of it, each its
 * own span with the region's ids (Yu-Han, 2026-10-22: one rule for every
 * finish), `topCut` where its top is the opening's edge rather than the
 * finish's own. A finish wholly inside an opening is not drawn. And it is cut
 * where the take-off cuts it (`objects`, by its own rule —
 * `finishCutsOnFace`): behind a cabinet or vanity, not behind a toilet, so
 * what is drawn is what is billed.
 *
 * @param {Object} wall
 * @param {{cardKind?: ?('kitchen'|'bathroom'), openings?: Array,
 *   objects?: Array}} [opts]
 */
export function wallFinishSpans(
  wall,
  { cardKind = null, openings = [], objects = [] } = {}
) {
  const regions = wall?.texture?.regions;
  if (!Array.isArray(regions) || !regions.length) return [];
  const frame = getWallCoordinateSystem(wall);
  if (!frame?.wallRight) return [];
  const right = { x: frame.wallRight.x, z: frame.wallRight.z };
  const range = faceRange(wall, right);
  if (!range) return [];
  const len = range.maxU - range.minU;
  const height = range.maxV - range.minV;
  // the face's plan line: its centre with the along-wall part taken out
  const c = wall.center || { x: 0, z: 0 };
  const cu = (c.x || 0) * right.x + (c.z || 0) * right.z;
  const base = { x: (c.x || 0) - right.x * cu, z: (c.z || 0) - right.z * cu };
  const at = (u) => ({ x: base.x + right.x * u, z: base.z + right.z * u });

  const holes = openingHolesOnFace(wall, openings, right, range);
  const cuts = finishCutsOnFace(wall, objects, right, range);
  return regions
    .filter((r) => r && isOverlayRegion(r) && regionHasAssignedFinish(r))
    .flatMap((region, i) => {
      const rect = regionRectOnWall(region, len, height);
      if (!(rect.a1 > rect.a0) || !(rect.y1 > rect.y0)) return [];
      const { kind, label } = finishKindOf(region);
      const key = `${region.regionId || region.id || 'region'}-${i}`;
      const span = {
        kind,
        label,
        color: kind === 'paint' ? paintHex(region) : null,
        wallId: wall.id ?? null,
        regionId: region.regionId ?? null,
        band:
          cardBandOf(region, cardKind) ??
          (isTileSection(region) ? SECTION_BAND : null),
        groupId: region.groupId ?? null,
      };
      const pieces = cutRect(rect, [
        ...holes,
        ...(kind === 'paint' ? cuts.paint : cuts.tile),
      ]);
      return pieces.map((p, k) => ({
        ...span,
        // an uncut finish keeps the key it always had
        key: pieces.length === 1 && p === rect ? key : `${key}-c${k}`,
        // a piece whose top is an opening's edge, not the finish's own top —
        // nothing to drag there
        topCut: p.y1 < rect.y1,
        a: at(range.minU + p.a0),
        b: at(range.minU + p.a1),
        bottom: p.y0,
        top: p.y1,
      }));
    });
}

/**
 * The drawn face in the shape Edit wall builds its snap lines on
 * (`wallRegionFrame` — the face mesh's extent along `right` and up): a
 * section's percentages are of that face, and the mesh is built from the
 * same polygon (or box) `faceRange` reads, so no mesh is needed (PR 5).
 *
 * @returns {?{right, up, uMin, uMax, vMin, vMax, start, end}} null for a
 *   wall with no face
 */
export function wallFaceFrame(wall) {
  if (!wall) return null;
  const frame = getWallCoordinateSystem(wall);
  if (!frame?.wallRight) return null;
  const right = { x: frame.wallRight.x, y: 0, z: frame.wallRight.z };
  const range = faceRange(wall, right);
  if (!range) return null;
  const c = wall.center || { x: 0, z: 0 };
  const cu = (c.x || 0) * right.x + (c.z || 0) * right.z;
  const at = (u) => ({
    x: (c.x || 0) - right.x * cu + right.x * u,
    z: (c.z || 0) - right.z * cu + right.z * u,
  });
  return {
    right,
    up: { x: 0, y: 1, z: 0 },
    uMin: range.minU,
    uMax: range.maxU,
    vMin: range.minV,
    vMax: range.maxV,
    start: at(range.minU),
    end: at(range.maxU),
  };
}

/**
 * Crossing a height between world Y and the drawing (call item #28): metres
 * above the drawing's floor line — the DRAWN face's bottom (`faceRange`, the
 * frame a region's percentages are read in). Every band writer measures in
 * that frame too (`@shared-lib/geometry/wallFace`), so a line the builder
 * would draw a band to at world Y is drawn exactly at `toDrawing(Y)`, even on
 * a polygon wall whose face is not its box. Until 2026-10-26 the writers
 * measured against the STORED height and this scaled by it, which matched
 * the writers and missed the counter: a backsplash on a face that follows a
 * raised ceiling stood 0.94" above it.
 *
 * @returns {?{toDrawing: (worldY) => number, toWorld: (m) => number}} null
 *   for a wall with no face or no frame
 */
export function bandHeightFrame(wall) {
  if (!wall) return null;
  const frame = getWallCoordinateSystem(wall);
  if (!frame?.wallRight) return null;
  const range = faceRange(wall, { x: frame.wallRight.x, z: frame.wallRight.z });
  if (!range) return null;
  return {
    toDrawing: (worldY) => worldY - range.minV,
    toWorld: (m) => range.minV + m,
  };
}

/**
 * Where a span sits on a drawn elevation segment, in px — the end-on band's
 * placement: the span's ends projected on `right` from `origin` (the
 * segment's wall centre), offset by the segment's own place in the drawing;
 * heights from the container's floor line, its bottom edge.
 *
 * @param {Object} span  one of `wallFinishSpans`
 * @param {Object} at
 * @param {number} at.segmentX      the segment's left edge, metres
 * @param {number} at.segmentWidth  the segment's drawn width, metres
 * @param {{x:number,z:number}} at.origin  the segment wall's centre
 * @param {{x:number,z:number}} at.right   the basis objects are drawn with
 * @param {number} at.maxHeight     the drawing's height, metres
 * @param {number} at.scale         px per metre
 */
export function placeWallFinish(
  span,
  { segmentX, segmentWidth, origin, right, maxHeight, scale }
) {
  const along = (p) => (p.x - origin.x) * right.x + (p.z - origin.z) * right.z;
  const s0 = along(span.a);
  const s1 = along(span.b);
  const top = Math.min(span.top, maxHeight);
  const bottom = Math.min(span.bottom, top);
  return {
    left: (segmentX + Math.min(s0, s1) + segmentWidth / 2) * scale,
    width: Math.abs(s1 - s0) * scale,
    top: (maxHeight - top) * scale,
    height: (top - bottom) * scale,
  };
}
